import * as THREE from 'three';
import type { UnitSystem } from '@/core/Units';
import type { GroundCoverState } from '@/environment/EnvironmentState';
import {
  type Heightfield,
  type TerrainGenerationParams,
  type TerrainProjectState,
  sampleHeightWorld,
} from './TerrainTypes';
import { generateHeightfield } from './TerrainGenerator';
import { buildTerrainGeometry, updateTerrainGeometryRegion } from './TerrainGeometry';
import { applySlopeTint, createTerrainMaterial, SurfaceTextures } from './TerrainMaterial';
import {
  deserializeSurfaceMap,
  dominantLayerAt,
  serializeSurfaceMap,
  surfacePalette,
  SurfaceMap,
  surfaceCoverage,
  type SurfaceCoverage,
  type SurfaceLayerId,
  type SurfaceRect,
} from './TerrainSurface';

export interface TerrainOptions {
  /** Hand-graded heights from a project file. Used verbatim when the shape matches the params. */
  bakedHeights?: readonly number[];
  /** Painted ground from a project file. */
  surface?: SurfaceMap;
}

/**
 * Owns a terrain's canonical heightfield and paint map (domain data) and its Three.js mesh (view).
 * The mesh is always reconstructed from that data, honoring the "Three.js is never the source of
 * truth" rule (spec §5, §15.1).
 *
 * Two independent grids describe the ground: heights on the mesh's own vertex lattice, and material
 * weights on a finer lattice in UV space. Keeping them separate is what lets the sculpt tool re-grade
 * a slope without disturbing the beds painted on it, and lets the paint be finer than the mesh
 * without paying for geometry nobody asked for.
 */
export class Terrain {
  readonly params: TerrainGenerationParams;
  readonly field: Heightfield;
  readonly surface: SurfaceMap;
  readonly mesh: THREE.Mesh;
  private readonly textures: SurfaceTextures;
  /** Palette revision the GPU was last brought in step with. */
  private paletteRevision: number;
  private readonly unsubscribePalette: () => void;
  private units: UnitSystem;

  constructor(
    params: TerrainGenerationParams,
    units: UnitSystem = 'imperial',
    options: TerrainOptions = {},
  ) {
    this.params = params;
    this.units = units;
    this.field = generateHeightfield(params);
    // A file that carries hand-graded heights is authoritative — the generator only supplies the
    // shape of the lattice. A length mismatch means the file was saved at a different segment count,
    // in which case the generated ground is the honest answer rather than a resampled guess.
    if (options.bakedHeights && options.bakedHeights.length === this.field.heights.length) {
      this.field.heights.set(options.bakedHeights);
    }

    this.surface =
      options.surface ??
      SurfaceMap.forSite(
        this.field.widthMeters,
        this.field.lengthMeters,
        undefined,
        surfacePalette.pages,
      );
    // A map handed in from a project file was sized for the palette *that file* carried; the palette
    // in force now may need more room, and growing costs nothing when it does not.
    this.surface.ensurePages(surfacePalette.pages);

    const geometry = buildTerrainGeometry(this.field);
    applySlopeTint(geometry);
    const material = createTerrainMaterial();
    this.textures = new SurfaceTextures(this.surface);
    this.textures.attach(material);
    this.paletteRevision = surfacePalette.revision;
    // Adding a material has to reach the ground without the caller knowing the renderer exists —
    // the forge, the swatch upload and the pipeline all just add to the palette.
    this.unsubscribePalette = surfacePalette.onChange(() => this.syncPalette());

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'terrain';
    this.mesh.receiveShadow = true;
    this.mesh.rotation.y = THREE.MathUtils.degToRad(params.northDegrees);
  }

  /**
   * Bring the ground in step with the palette: grow the map if a new material needs another page,
   * then re-read colours, roughness and swatches. Cheap and idempotent when nothing has changed.
   */
  syncPalette(): void {
    if (this.paletteRevision === surfacePalette.revision) return;
    this.paletteRevision = surfacePalette.revision;
    this.surface.ensurePages(surfacePalette.pages);
    this.textures.sync(this.surface);
  }

  /**
   * Lay winter cover over the ground, or take it off. Costs a uniform change and, the first time a
   * variant is asked for, one texture upload — the paint map is not touched, so a site's materials
   * survive a winter untouched and come back exactly as they were.
   */
  setWinterCover(cover: GroundCoverState | null): void {
    this.textures.setWinterCover(cover);
  }

  /**
   * Take a layer's paint back out of the ground, returning the rectangle that changed.
   *
   * Retiring a material is two separate facts — it leaves the palette, and the weights that referred
   * to it stop meaning anything — and only the second is undoable. So this does the paint half and
   * hands the rectangle back for the caller to record; the palette half is not on the history,
   * because a material's *existence* is not a property of the garden.
   */
  clearSurfaceChannel(channel: number): SurfaceRect | null {
    if (!this.surface.clearChannel(channel)) return null;
    this.textures.touch();
    return { x: 0, y: 0, width: this.surface.width, height: this.surface.height };
  }

  /** Interpolated surface height at terrain-local (x, z) in meters. */
  sampleHeight(x: number, z: number): number {
    return sampleHeightWorld(this.field, x, z);
  }

  /** The ground material that reads as dominant at a terrain-local point. */
  sampleSurface(x: number, z: number): SurfaceLayerId {
    return dominantLayerAt(this.surface, x, z);
  }

  /** Fraction of the site covered by each ground material, summing to 1. */
  coverage(): SurfaceCoverage {
    return surfaceCoverage(this.surface);
  }

  get widthMeters(): number {
    return this.field.widthMeters;
  }

  get lengthMeters(): number {
    return this.field.lengthMeters;
  }

  /**
   * World point → terrain-local. The mesh is rotated by the site's north orientation, so every tool
   * that works in grid space has to undo that first; doing it here means there is one copy of the
   * trigonometry rather than one per tool.
   */
  toLocal(x: number, z: number, target = new THREE.Vector2()): THREE.Vector2 {
    const angle = -this.mesh.rotation.y;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return target.set(x * cos + z * sin, -x * sin + z * cos);
  }

  /** Terrain-local point → world. The inverse of {@link toLocal}. */
  toWorld(x: number, z: number, target = new THREE.Vector2()): THREE.Vector2 {
    const angle = this.mesh.rotation.y;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return target.set(x * cos + z * sin, -x * sin + z * cos);
  }

  /**
   * Push a region of the heightfield back into the mesh after a sculpt edit. Cheap enough to call
   * once per frame during a stroke; the bounding volumes are deliberately left until
   * {@link finishHeightEdit}, since they are only needed by raycasting and frustum culling and
   * recomputing them per frame would dominate the cost of the edit.
   */
  applyHeightRegion(rect: SurfaceRect): void {
    updateTerrainGeometryRegion(this.mesh.geometry, this.field, rect);
  }

  /** Settle the mesh after a stroke ends: bounding volumes back in step with the new ground. */
  finishHeightEdit(): void {
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();
  }

  /** Re-upload the paint map. Called when the paint tool has changed weights. */
  applySurfaceRegion(): void {
    this.textures.touch();
  }

  /** Serialize to canonical project state (bakes heights and paint so reload is exact). */
  toProjectState(): TerrainProjectState {
    const painted = !this.surface.isEmpty();
    return {
      generation: { ...this.params },
      units: this.units,
      bakedHeights: Array.from(this.field.heights),
      ...(painted ? { surface: serializeSurfaceMap(this.surface) } : {}),
    };
  }

  dispose(): void {
    this.unsubscribePalette();
    this.mesh.geometry.dispose();
    this.textures.dispose();
    const material = this.mesh.material;
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose());
    } else {
      material.dispose();
    }
  }

  /** Reconstruct a terrain from canonical project state, hand-grading and paint included. */
  static fromProjectState(state: TerrainProjectState): Terrain {
    return new Terrain(state.generation, state.units, {
      ...(state.bakedHeights ? { bakedHeights: state.bakedHeights } : {}),
      surface: deserializeSurfaceMap(
        state.surface,
        state.generation.widthMeters,
        state.generation.lengthMeters,
        undefined,
        surfacePalette.pages,
      ),
    });
  }
}
